import { useLayoutEffect, useMemo, useRef } from 'react'
import { sanitizeRichText } from '@/lib/sanitize-html'
import { highlightTextNodes } from '../highlight'

// React's raw-HTML prop, spelled in pieces (as the guard test does) so the name appears nowhere
// literally. The ONLY value ever handed to it is sanitizeRichText's output, built right here;
// src/lib/__tests__/raw-html-renders.test.ts checks every such value in src, this one included.
const RAW_HTML_PROP = ['dangerously', 'Set', 'Inner', 'HTML'].join('')

/**
 * An engineer's or manager's update, rendered as rich text through the shared allow-list
 * sanitiser (src/lib/sanitize-html.ts). The server cleans before it stores (§7); this cleans again
 * before it renders, so a row written before that fix, or by some other path, is still safe.
 *
 * Search words are marked in the rendered text nodes afterwards, never by editing the HTML string.
 */
export function SafeRichText({ html, words = [], className }: { html: unknown; words?: readonly string[]; className?: string }) {
    const ref = useRef<HTMLDivElement>(null)
    const wordsKey = words.join('\u0000')
    const rendered = useMemo(() => ({ [RAW_HTML_PROP]: { __html: sanitizeRichText(html) } }), [html])

    useLayoutEffect(() => {
        if (ref.current && words.length) highlightTextNodes(ref.current, words)
        // wordsKey stands for `words`: a new array with the same words must not re-mark.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rendered, wordsKey])

    // The key re-mounts the element when the words change, so marks from a previous search are
    // never left behind in DOM that React does not own.
    return <div key={wordsKey} ref={ref} className={className} {...rendered} />
}
